/**
 * Computed capability profile for /v1/playback/decisions — feature
 * detection over UA sniffing, so the document says what THIS
 * browser/OS/hardware decodes.
 *
 * Two probe surfaces, because the server reads the document two ways:
 *  - `directPlay[]` gates progressive `<video src>` playback of the whole
 *    file (HTMLMediaElement.canPlayType) AND, through its video lists,
 *    which video codecs may be COPIED into an fMP4/HLS remux.
 *  - `hlsAudioCopy[]` is the MSE side only: what
 *    (ManagedMediaSource ?? MediaSource).isTypeSupported accepts inside
 *    fMP4 — the exact call hls.js 1.6 makes. Chrome plays MP3-in-MP4
 *    progressively but rejects it through MediaSource, which is why the
 *    two lists are probed separately.
 * hls.js runs whenever a MediaSource exists (desktop Safari included; iOS
 * 17.1+ through ManagedMediaSource); the native-HLS branch (`mse: null`)
 * is iPhone Safari before 17.1.
 *
 * Video range (HDR10 / HDR10+ / HLG / Dolby Vision) rides
 * `codecLimits[].videoRanges` in the server's vocabulary (Jellyfin's
 * VideoRangeType names): PQ/HLG come from MediaCapabilities
 * (`transferFunction`), Dolby Vision from the `dvh1.05.06` / `dvh1.08.06`
 * probes on the MSE surface — Chrome answers false to every dvh1 string
 * and plays profile 8 as its HDR10 base layer, Safari answers for itself.
 * `codecLimits[].tags` lists the sample entries the progressive decoder
 * takes (Safari: hvc1|dvh1, jellyfin-web's rule; Chromium: hvc1|hev1).
 *
 * The shape mirrors the server's data-driven profile documents
 * (rago-server internal/decision/profiles) so it parses as an inline
 * profile. Container names are ffprobe's FIRST format name — "mov" for
 * the mp4/m4v/mov family, "matroska" for mkv AND webm; the server folds
 * the usual aliases, but only the canonical names are sent.
 */

export interface CapabilityProfile {
  id: string;
  directPlay: { containers: string[]; video: string[]; audio: string[] }[];
  codecLimits: {
    codec: string;
    maxLevel?: number;
    maxBitDepth?: number;
    maxChannels?: number;
    videoRanges?: string[];
    tags?: string[];
  }[];
  subtitles: { text: string[]; image: string[] };
  hlsAudioCopy: string[];
  transcodeTargets: { video: string; maxLevel: number; audio: string; maxAudioChannels: number };
}

export interface CapabilityProbes {
  /**
   * `(ManagedMediaSource ?? MediaSource).isTypeSupported` — the surface
   * hls.js feeds; null when hls.js would fall back to native HLS.
   */
  mse: ((mime: string) => boolean) | null;
  /** `HTMLMediaElement.canPlayType(mime) !== ""` — progressive `<video src>`. */
  element: (mime: string) => boolean;
}

/** What one codec's decoder renders beyond SDR (MediaCapabilities, 4K24). */
export interface RangeSupport {
  pq: boolean;
  hlg: boolean;
}

export interface CapabilityFacts {
  /** `AudioContext.destination.maxChannelCount` — what the output device takes. */
  outputChannels?: number;
  /** H.264 High 10 decodes smoothly here (MediaCapabilities); undefined = assume not. */
  h264TenBit?: boolean;
  /** PQ / HLG per codec; a missing codec is SDR only. */
  hdr?: { hevc?: RangeSupport; av1?: RangeSupport; vp9?: RangeSupport };
  /** Apple WebKit: the progressive decoder wants hvc1|dvh1 sample entries. */
  appleWebKit?: boolean;
}

type Probe = (mime: string) => boolean;

const MP4 = (codecs: string) => `video/mp4; codecs="${codecs}"`;
const MP4A = (codecs: string) => `audio/mp4; codecs="${codecs}"`;
const WEBM = (codecs: string) => `video/webm; codecs="${codecs}"`;

/** Server vocabulary (rago-server internal/decision/videorange.go). */
export const VIDEO_RANGES = {
  sdr: "SDR",
  hdr10: "HDR10",
  hdr10Plus: "HDR10Plus",
  hlg: "HLG",
  dovi: "DOVI",
  doviWithHdr10: "DOVIWithHDR10",
  doviWithHdr10Plus: "DOVIWithHDR10Plus",
  doviWithHlg: "DOVIWithHLG",
  doviWithSdr: "DOVIWithSDR",
} as const;

/**
 * Transcoded surround follows the output device (Kenny's call): a 7.1
 * device gets 8 channels, 5.1 gets 6, everything else — including an
 * unknown device — stereo.
 */
export function maxAudioChannelsFor(outputChannels: number | undefined): number {
  if (!outputChannels || outputChannels < 6) return 2;
  return outputChannels >= 8 ? 8 : 6;
}

/** Pure: the document for a set of probe answers and device facts. */
export function buildCapabilityProfile(
  probes: CapabilityProbes,
  facts: CapabilityFacts = {},
): CapabilityProfile {
  const dec = probes.element;
  const hls = probes.mse ?? probes.element;
  const any = (probe: Probe, ...mimes: string[]) => mimes.some((m) => probe(m));
  // Video must pass both surfaces: `directPlay.video` is the progressive
  // gate AND the server's copy gate (profiles.go VideoDecodable).
  const video = (...mimes: string[]) => any(dec, ...mimes) && any(hls, ...mimes);

  const h264 = video(MP4("avc1.640029"));
  const h264Level = video(MP4("avc1.640834")) ? 52 : video(MP4("avc1.640833")) ? 51 : 42;
  const hevcMain = video(MP4("hvc1.1.6.L123.B0"), MP4("hev1.1.6.L123.B0"));
  const hevcMain10 = video(MP4("hvc1.2.4.L123.B0"), MP4("hev1.2.4.L123.B0"));
  const hevc = hevcMain || hevcMain10;
  // ffprobe reports HEVC levels ×30 (123 = 4.1, 153 = 5.1, 183 = 6.1).
  const hevcTier = hevcMain10 ? "2.4" : "1.6";
  const hevcLevel = video(MP4(`hvc1.${hevcTier}.L183.B0`))
    ? 183
    : video(MP4(`hvc1.${hevcTier}.L153.B0`))
      ? 153
      : 123;
  const av1 = video(MP4("av01.0.08M.08"));
  const av1TenBit = video(MP4("av01.0.08M.10"));
  const vp9Mp4 = video(MP4("vp09.00.10.08"));
  const vp9Webm = video(WEBM("vp9"));
  const av1Webm = video(WEBM("av01.0.08M.08"));
  // Dolby Vision through the engine that will play it (jellyfin-web's
  // two probes). Profile 8 is declared as the set of base layers the
  // decoder shows; profile 5 stands alone (no compatible layer).
  const doviP5 = hevcMain10 && hls(MP4("dvh1.05.06"));
  const doviP8 = hevcMain10 && hls(MP4("dvh1.08.06"));

  // Progressive audio — what <video src> decodes inside an mp4. Spellings
  // differ per browser (Chrome: flac+fLaC, opus+Opus, mp4a.6B but not 6b).
  const aac = dec(MP4A("mp4a.40.2"));
  const mp3 = any(dec, MP4A("mp4a.6B"), MP4A("mp3"));
  const flac = any(dec, MP4A("flac"), MP4A("fLaC"));
  const opus = any(dec, MP4A("opus"), MP4A("Opus"));
  const ac3 = dec(MP4A("ac-3"));
  const eac3 = dec(MP4A("ec-3"));
  const opusWebm = dec(WEBM("opus"));
  const vorbisWebm = dec(WEBM("vorbis"));

  // fMP4 audio through the HLS engine. Never `mp4a.40.34` (a wildcard
  // match on some engines, a parser failure on all) and never `audio/mpeg`
  // (a raw MP3 stream, not a track inside fMP4). Without MSE, Apple's own
  // HLS probe (jellyfin-web's Safari rule) answers for MP3.
  const hlsAac = hls(MP4A("mp4a.40.2"));
  const hlsMp3 = probes.mse
    ? any(probes.mse, MP4A("mp3"), MP4A("mp4a.6B"))
    : dec('application/vnd.apple.mpegurl; codecs="avc1.42E01E, mp4a.40.34"');
  const hlsFlac = any(hls, MP4A("flac"), MP4A("fLaC"));
  const hlsOpus = any(hls, MP4A("opus"), MP4A("Opus"));
  const hlsAc3 = hls(MP4A("ac-3"));
  const hlsEac3 = hls(MP4A("ec-3"));

  const pick = (...entries: [string, boolean][]) =>
    entries.filter(([, ok]) => ok).map(([name]) => name);

  const directPlay: CapabilityProfile["directPlay"] = [];
  const mp4Video = pick(["h264", h264], ["hevc", hevc], ["av1", av1], ["vp9", vp9Mp4]);
  if (mp4Video.length > 0) {
    // Audio may be empty: an audio-less file matches a nil audio stream.
    directPlay.push({
      containers: ["mov"],
      video: mp4Video,
      audio: pick(
        ["aac", aac],
        ["mp3", mp3],
        ["flac", flac],
        ["opus", opus],
        ["ac3", ac3],
        ["eac3", eac3],
      ),
    });
  }
  const webmVideo = pick(["vp9", vp9Webm], ["av1", av1Webm]);
  if (webmVideo.length > 0) {
    // "matroska" is .webm AND .mkv on the server. h264 stays out on
    // purpose: an MKV rides the HLS remux (Plex behaviour), never <video src>.
    directPlay.push({
      containers: ["matroska"],
      video: webmVideo,
      audio: pick(["opus", opusWebm], ["vorbis", vorbisWebm]),
    });
  }

  // Video ranges per codec: SDR always; PQ (HDR10, and HDR10+ — every
  // HDR10 decoder plays it, the dynamic metadata just goes unused) and HLG
  // from MediaCapabilities; Dolby Vision only for hevc and only where the
  // engine said so. Profile 7 / out-of-spec records are never listed —
  // the server strips them to HDR10 for us.
  const rangesFor = (codec: "hevc" | "av1" | "vp9", dolby: boolean): string[] => {
    const r: string[] = [VIDEO_RANGES.sdr];
    const h = facts.hdr?.[codec];
    if (h?.pq) r.push(VIDEO_RANGES.hdr10, VIDEO_RANGES.hdr10Plus);
    if (h?.hlg) r.push(VIDEO_RANGES.hlg);
    if (dolby && doviP5) r.push(VIDEO_RANGES.dovi);
    if (dolby && doviP8) {
      r.push(
        VIDEO_RANGES.doviWithHdr10,
        VIDEO_RANGES.doviWithHdr10Plus,
        VIDEO_RANGES.doviWithHlg,
        VIDEO_RANGES.doviWithSdr,
      );
    }
    return r;
  };

  const codecLimits: CapabilityProfile["codecLimits"] = [];
  if (h264) {
    codecLimits.push({
      codec: "h264",
      maxLevel: h264Level,
      maxBitDepth: facts.h264TenBit ? 10 : 8,
      videoRanges: [VIDEO_RANGES.sdr],
    });
  }
  if (hevc) {
    codecLimits.push({
      codec: "hevc",
      maxLevel: hevcLevel,
      ...(hevcMain10 ? {} : { maxBitDepth: 8 }),
      videoRanges: rangesFor("hevc", true),
      tags: facts.appleWebKit ? ["hvc1", "dvh1"] : ["hvc1", "hev1"],
    });
  }
  if (av1 || av1Webm) {
    codecLimits.push({
      codec: "av1",
      ...(av1TenBit ? {} : { maxBitDepth: 8 }),
      videoRanges: rangesFor("av1", false),
    });
  }
  if (vp9Mp4 || vp9Webm) {
    codecLimits.push({ codec: "vp9", videoRanges: rangesFor("vp9", false) });
  }
  // 7.1 AAC decodes on every engine we run (ffmpeg, AudioToolbox); the
  // server's built-in profiles stay at 6.
  codecLimits.push({ codec: "aac", maxChannels: 8 });

  return {
    id: "web-inline",
    directPlay,
    codecLimits,
    // `text` is informational — the server converts every text codec to a
    // WebVTT sidecar regardless; the empty `image` list is what burns
    // PGS/VobSub into the picture.
    subtitles: { text: ["subrip", "ass", "ssa", "webvtt", "mov_text"], image: [] },
    hlsAudioCopy: pick(
      ["aac", hlsAac],
      ["mp3", hlsMp3],
      ["flac", hlsFlac],
      ["opus", hlsOpus],
      ["ac3", hlsAc3],
      ["eac3", hlsEac3],
    ),
    transcodeTargets: {
      video: "h264",
      maxLevel: h264Level,
      audio: "aac",
      maxAudioChannels: maxAudioChannelsFor(facts.outputChannels),
    },
  };
}

interface MediaSourceLike {
  isTypeSupported(mime: string): boolean;
}

/** The live browser's probes; node-safe (everything false, no MSE). */
export function browserProbes(): CapabilityProbes {
  if (typeof document === "undefined") return { mse: null, element: () => false };
  const w = globalThis as {
    ManagedMediaSource?: MediaSourceLike;
    MediaSource?: MediaSourceLike;
    WebKitMediaSource?: MediaSourceLike;
  };
  // hls.js 1.6 (preferManagedMediaSource: true) picks the same source.
  const ms = w.ManagedMediaSource ?? w.MediaSource ?? w.WebKitMediaSource;
  const mse =
    ms && typeof ms.isTypeSupported === "function"
      ? (mime: string) => {
          try {
            return ms.isTypeSupported(mime);
          } catch {
            return false;
          }
        }
      : null;
  const video = document.createElement("video");
  const element = (mime: string) => {
    try {
      return video.canPlayType(mime) !== "";
    } catch {
      return false;
    }
  };
  return { mse, element };
}

/** Device facts the probes cannot express. Never rejects; unknown = conservative. */
export async function detectFacts(probes: CapabilityProbes): Promise<CapabilityFacts> {
  const facts: CapabilityFacts = {};
  try {
    const g = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = g.AudioContext ?? g.webkitAudioContext;
    if (Ctor) {
      const ctx = new Ctor();
      facts.outputChannels = ctx.destination.maxChannelCount;
      void ctx.close().catch(() => {});
    }
  } catch {
    // No Web Audio (or a blocked context): unknown device → stereo.
  }
  if (typeof navigator !== "undefined" && navigator.vendor === "Apple Computer, Inc.") {
    facts.appleWebKit = true;
  }
  try {
    facts.h264TenBit = await probeH264TenBit(probes);
  } catch {
    // Unknown → 8-bit.
  }
  try {
    facts.hdr = await probeHDR(probes);
  } catch {
    // Unknown → SDR only.
  }
  return facts;
}

const MEDIA_CAPABILITIES_TIMEOUT_MS = 1500;

async function decodingInfo(
  probes: CapabilityProbes,
  video: MediaDecodingConfiguration["video"],
): Promise<MediaCapabilitiesDecodingInfo | undefined> {
  if (typeof navigator === "undefined" || !navigator.mediaCapabilities?.decodingInfo) return undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.mediaCapabilities.decodingInfo({ type: probes.mse ? "media-source" : "file", video }),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error("mediaCapabilities timeout")), MEDIA_CAPABILITIES_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function probeH264TenBit(probes: CapabilityProbes): Promise<boolean | undefined> {
  if (typeof navigator === "undefined") return undefined;
  // Safari's probes claim High 10 but VideoToolbox does not decode it, and
  // phones software-decode it too slowly — jellyfin-web's exceptions.
  if (navigator.vendor === "Apple Computer, Inc.") return false;
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile || /Mobi|Android/i.test(navigator.userAgent)) return false;
  const info = await decodingInfo(probes, {
    contentType: 'video/mp4; codecs="avc1.6e0033"',
    width: 1920,
    height: 1080,
    bitrate: 8_000_000,
    framerate: 24,
  });
  return info ? info.supported && info.smooth : undefined;
}

/**
 * PQ and HLG per codec at 4K24 — `transferFunction` is the only HDR field
 * that carries signal (Chromium answers `hdrMetadataType: smpteSt2086`
 * true unconditionally and every dynamic type false). Chrome/macOS
 * 2026-09: hevc + vp9 yes, av1 no.
 */
async function probeHDR(probes: CapabilityProbes): Promise<CapabilityFacts["hdr"]> {
  const codecs: Array<["hevc" | "av1" | "vp9", string]> = [
    ["hevc", MP4("hvc1.2.4.L153.B0")],
    ["av1", MP4("av01.0.13M.10")],
    ["vp9", MP4("vp09.02.10.10.01.09.16.09.01")],
  ];
  const results = await Promise.all(
    codecs.map(async ([codec, contentType]) => {
      const one = async (transferFunction: "pq" | "hlg") => {
        const info = await decodingInfo(probes, {
          contentType,
          width: 3840,
          height: 2160,
          bitrate: 20_000_000,
          framerate: 24,
          transferFunction,
          colorGamut: "rec2020",
        });
        return Boolean(info?.supported && info.smooth);
      };
      const [pq, hlg] = await Promise.all([one("pq").catch(() => false), one("hlg").catch(() => false)]);
      return [codec, { pq, hlg }] as const;
    }),
  );
  const out: CapabilityFacts["hdr"] = {};
  for (const [codec, support] of results) out[codec] = support;
  return out;
}

let cached: Promise<CapabilityProfile> | null = null;
let watching = false;

/**
 * The profile for this browser, detected once and cached. Call it after a
 * user gesture (the player mounts on Play) — the AudioContext behind
 * `outputChannels` is created there.
 */
export function getCapabilityProfile(): Promise<CapabilityProfile> {
  if (!cached) {
    const probes = browserProbes();
    cached = detectFacts(probes).then((facts) => buildCapabilityProfile(probes, facts));
    watchOutputDevices();
  }
  return cached;
}

/** A receiver plugged in later changes maxAudioChannels: re-detect on the next Play. */
function watchOutputDevices() {
  if (watching) return;
  watching = true;
  try {
    navigator.mediaDevices?.addEventListener("devicechange", () => {
      cached = null;
    });
  } catch {
    // No mediaDevices (node, insecure context): the first detection stands.
  }
}
